/**
 * ClubLights — SVG + CSS techno-club visuals:
 *   • 3 sweeping colored light cones from an implied DJ booth
 *   • Horizontal laser scan line
 *   • Rare strobe flash
 *   • Drifting haze / smoke
 *   • Crowd silhouette with bobbing heads
 *
 * Purely decorative. pointer-events-none. Composed over the existing
 * sun + dune layers in Hero.
 */
export default function ClubLights() {
  return (
    <div
      className="absolute inset-0 pointer-events-none overflow-hidden"
      aria-hidden="true"
      data-testid="club-lights"
    >
      {/* ===== Smoke / haze drifting ===== */}
      <div
        className="absolute inset-0 opacity-60"
        style={{
          background:
            "radial-gradient(40% 30% at 30% 75%, rgba(234,88,12,0.28), transparent 60%)," +
            "radial-gradient(35% 25% at 70% 80%, rgba(157,23,77,0.35), transparent 60%)," +
            "radial-gradient(30% 20% at 50% 68%, rgba(34,211,238,0.18), transparent 60%)",
          filter: "blur(40px)",
          animation: "haze-drift 18s ease-in-out infinite alternate",
          willChange: "transform",
        }}
      />

      {/* ===== 3 sweeping light beams from an implied stage point above dune ===== */}
      {/* origin: 50% 72% on the page (just above dunes) */}
      <div
        className="absolute left-1/2 top-[72%] -translate-x-1/2 -translate-y-full w-px h-px"
        style={{
          transform: "translate(-50%, -100%)",
        }}
      >
        {/* Each beam is an elongated triangle via clip-path with a linear gradient,
            anchored at bottom-center, rotating around that point. */}
        {[
          { color: "rgba(234,88,12,0.55)", dur: "9s", start: -35, delay: "0s" },
          { color: "rgba(34,211,238,0.50)", dur: "11s", start: 0, delay: "-3s" },
          { color: "rgba(225,29,116,0.55)", dur: "10s", start: 35, delay: "-6s" },
        ].map((b, i) => (
          <div
            key={i}
            className="absolute left-0 top-0"
            style={{
              width: "90vmax",
              height: "95vmax",
              transformOrigin: "0% 100%",
              transform: `translate(-50%, -100%) rotate(${b.start}deg)`,
              animation: `beam-sweep-${i} ${b.dur} ease-in-out ${b.delay} infinite alternate`,
            }}
          >
            <div
              style={{
                width: "100%",
                height: "100%",
                background: `linear-gradient(to top, ${b.color} 0%, ${b.color.replace(
                  /[\d.]+\)$/,
                  "0.15)"
                )} 40%, transparent 75%)`,
                clipPath: "polygon(48% 100%, 52% 100%, 100% 0%, 0% 0%)",
                filter: "blur(18px)",
                mixBlendMode: "screen",
              }}
            />
          </div>
        ))}

        {/* Tight core beam overlay for brightness */}
        <div
          className="absolute left-0 top-0"
          style={{
            width: "10vmax",
            height: "80vmax",
            transformOrigin: "50% 100%",
            transform: "translate(-50%, -100%)",
            background:
              "linear-gradient(to top, rgba(255,247,237,0.35), transparent 60%)",
            filter: "blur(8px)",
            mixBlendMode: "screen",
            animation: "beam-core 6s ease-in-out infinite alternate",
          }}
        />

        {/* Glow at beam origin (stage "lamp") */}
        <div
          className="absolute"
          style={{
            left: 0,
            top: 0,
            width: 60,
            height: 60,
            transform: "translate(-50%, -50%)",
            background:
              "radial-gradient(circle, rgba(255,247,237,0.85), rgba(234,88,12,0.4) 45%, transparent 70%)",
            filter: "blur(6px)",
            mixBlendMode: "screen",
            animation: "lamp-pulse 0.93s ease-in-out infinite",
          }}
        />
      </div>

      {/* ===== Horizontal laser scan line sweeping top->bottom ===== */}
      <div
        className="absolute left-0 right-0"
        style={{
          height: "2px",
          background:
            "linear-gradient(90deg, transparent, rgba(34,211,238,0.8) 20%, rgba(255,247,237,1) 50%, rgba(225,29,116,0.8) 80%, transparent)",
          boxShadow:
            "0 0 18px rgba(34,211,238,0.8), 0 0 30px rgba(225,29,116,0.6)",
          mixBlendMode: "screen",
          animation: "laser-scan 7s cubic-bezier(0.4,0,0.2,1) infinite",
        }}
      />

      {/* ===== Rare strobe flash ===== */}
      <div
        className="absolute inset-0 bg-white"
        style={{
          mixBlendMode: "overlay",
          animation: "strobe 11s steps(1) infinite",
        }}
      />

      {/* ===== Crowd silhouette with bobbing heads ===== */}
      <div className="absolute left-0 right-0 bottom-0 h-[18vh] md:h-[22vh] pointer-events-none">
        <svg
          viewBox="0 0 1440 220"
          preserveAspectRatio="none"
          className="absolute inset-0 w-full h-full"
        >
          <defs>
            <linearGradient id="crowdGrad" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#0C0A09" stopOpacity="0" />
              <stop offset="60%" stopColor="#0C0A09" stopOpacity="0.85" />
              <stop offset="100%" stopColor="#0C0A09" stopOpacity="1" />
            </linearGradient>
          </defs>

          {/* Back row — slower bob */}
          <g style={{ animation: "crowd-bob-slow 2.1s ease-in-out infinite" }}>
            {Array.from({ length: 42 }).map((_, i) => {
              const x = (i / 41) * 1440 + (i % 2 === 0 ? 4 : -4);
              const y = 140 + ((i * 13) % 10);
              const r = 7 + (i % 3);
              return (
                <g key={`b-${i}`} fill="#050302">
                  <circle cx={x} cy={y} r={r} />
                  <rect
                    x={x - r}
                    y={y}
                    width={r * 2}
                    height={90}
                    rx={r * 0.6}
                  />
                </g>
              );
            })}
          </g>

          {/* Front row — faster, slight hand-up variations */}
          <g style={{ animation: "crowd-bob-fast 1.35s ease-in-out infinite" }}>
            {Array.from({ length: 36 }).map((_, i) => {
              const x = (i / 35) * 1440 + ((i * 17) % 10) - 5;
              const y = 170 + ((i * 7) % 8);
              const r = 9 + (i % 3);
              const handsUp = i % 5 === 0;
              return (
                <g key={`f-${i}`} fill="#000">
                  <circle cx={x} cy={y} r={r} />
                  <rect
                    x={x - r}
                    y={y}
                    width={r * 2}
                    height={120}
                    rx={r * 0.7}
                  />
                  {handsUp && (
                    <>
                      <rect
                        x={x - r - 4}
                        y={y - 22}
                        width="4"
                        height="22"
                        rx="2"
                      />
                      <rect
                        x={x + r}
                        y={y - 22}
                        width="4"
                        height="22"
                        rx="2"
                      />
                    </>
                  )}
                </g>
              );
            })}
          </g>

          {/* Fade gradient at top of crowd so it blends with scene */}
          <rect x="0" y="0" width="1440" height="220" fill="url(#crowdGrad)" />
        </svg>
      </div>

      {/* ===== Keyframes (scoped via <style>) ===== */}
      <style>{`
        @keyframes haze-drift {
          0% { transform: translate3d(0, 0, 0); }
          100% { transform: translate3d(-3%, 1%, 0); }
        }
        @keyframes beam-sweep-0 {
          0% { transform: translate(-50%, -100%) rotate(-55deg); }
          100% { transform: translate(-50%, -100%) rotate(-15deg); }
        }
        @keyframes beam-sweep-1 {
          0% { transform: translate(-50%, -100%) rotate(-20deg); }
          100% { transform: translate(-50%, -100%) rotate(20deg); }
        }
        @keyframes beam-sweep-2 {
          0% { transform: translate(-50%, -100%) rotate(55deg); }
          100% { transform: translate(-50%, -100%) rotate(15deg); }
        }
        @keyframes beam-core {
          0%, 100% { opacity: 0.8; transform: translate(-50%, -100%) scaleY(1); }
          50% { opacity: 1; transform: translate(-50%, -100%) scaleY(1.05); }
        }
        @keyframes lamp-pulse {
          0%, 100% { opacity: 0.7; transform: translate(-50%, -50%) scale(1); }
          50% { opacity: 1; transform: translate(-50%, -50%) scale(1.15); }
        }
        @keyframes laser-scan {
          0% { top: 18%; opacity: 0; }
          10% { opacity: 1; }
          45% { top: 62%; opacity: 1; }
          55% { opacity: 0; }
          100% { top: 62%; opacity: 0; }
        }
        @keyframes strobe {
          0%, 98.5%, 100% { opacity: 0; }
          99%, 99.3% { opacity: 0.35; }
          99.15% { opacity: 0; }
        }
        @keyframes crowd-bob-slow {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-6px); }
        }
        @keyframes crowd-bob-fast {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-10px); }
        }
      `}</style>
    </div>
  );
}
